import { DEFAULT_VAD, initialVad, vadStep, type VadConfig, type VadState } from './vad';

/** Microphone capture (VOICE-CAPTURE, SDD §20.4).
 *
 *  Real: `getUserMedia`, a Web Audio analyser, and an RMS level per animation frame feeding `vadStep`. No provider and
 *  no network — hearing that somebody is talking is arithmetic on samples, and it works with no credentials at all.
 *
 *  What is *not* here is turning that audio into words. Transcription is a provider call (`transcribe.ts`), and there
 *  is no ASR credential in this build, so the capture is real and the words are the labelled simulated ones. The two
 *  are separate files precisely so that stays visible rather than becoming "voice input, sort of".
 *
 *  The audio source is injectable for the same reason the machine is a pure reducer: jsdom has no `AudioContext`, and
 *  a capture you can only test by speaking into it is a capture nobody tests the awkward cases of.
 */

export interface LevelSource {
  /** Current input level as RMS in 0..1. */
  read(): number;
  close(): void;
}

export interface CaptureEvents {
  onSpeechStart?: () => void;
  onEndpoint?: () => void;
  /** Nothing said within §20.4's silence window. */
  onTimeout?: () => void;
  /** Denied permission, no device, or a source that failed to open — the HUD degrades to push-to-talk. */
  onUnavailable?: (reason: string) => void;
}

export interface CaptureOptions extends CaptureEvents {
  readonly openSource?: () => Promise<LevelSource>;
  readonly config?: VadConfig;
  /** Frame interval. Real capture reads far faster than this; 50ms is enough resolution for a 700ms endpoint. */
  readonly frameMs?: number;
  readonly setInterval?: typeof globalThis.setInterval;
  readonly clearInterval?: typeof globalThis.clearInterval;
}

export interface Capture {
  start(): Promise<boolean>;
  stop(): void;
  /** Current VAD state — read by tests; the HUD renders no level, because the design has no meter. */
  state(): VadState;
}

/** The browser binding. Opens the microphone and reads RMS off an analyser.
 *
 *  `getUserMedia` prompts on first use and the prompt is the consent: the stream is opened when a turn starts and the
 *  tracks are stopped when it ends, so the browser's recording indicator tracks reality rather than staying lit for a
 *  session. A microphone held open between turns is a microphone nobody can tell is off. */
async function openMicrophone(): Promise<LevelSource> {
  const media = globalThis.navigator?.mediaDevices;
  if (!media?.getUserMedia) throw new Error('This browser has no microphone access.');

  const stream = await media.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
  });
  const Ctor =
    globalThis.AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) {
    for (const track of stream.getTracks()) track.stop();
    throw new Error('This browser has no Web Audio support.');
  }

  const context = new Ctor();
  const analyser = context.createAnalyser();
  // Small window: the level should follow the voice closely enough that a 120ms word registers.
  analyser.fftSize = 512;
  context.createMediaStreamSource(stream).connect(analyser);
  const samples = new Float32Array(analyser.fftSize);

  return {
    read() {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) sum += sample * sample;
      return Math.sqrt(sum / samples.length);
    },
    close() {
      for (const track of stream.getTracks()) track.stop();
      void context.close();
    },
  };
}

export function createCapture(options: CaptureOptions = {}): Capture {
  const frameMs = options.frameMs ?? 50;
  const config = options.config ?? DEFAULT_VAD;
  const open = options.openSource ?? openMicrophone;
  const setTimer = options.setInterval ?? globalThis.setInterval;
  const clearTimer = options.clearInterval ?? globalThis.clearInterval;

  let source: LevelSource | null = null;
  let timer: ReturnType<typeof globalThis.setInterval> | null = null;
  let vad = initialVad();

  const stop = (): void => {
    if (timer !== null) clearTimer(timer);
    timer = null;
    source?.close();
    source = null;
  };

  return {
    async start(): Promise<boolean> {
      stop();
      vad = initialVad();
      try {
        source = await open();
      } catch (error) {
        // Denied or unavailable is not a failure to report at the trader: push-to-talk still works, and telling them
        // the microphone is off is more use than an error.
        options.onUnavailable?.(error instanceof Error ? error.message : 'The microphone is unavailable.');
        return false;
      }

      timer = setTimer(() => {
        if (!source) return;
        vad = vadStep(vad, source.read(), frameMs, config);
        switch (vad.emit) {
          case 'speech.start':
            options.onSpeechStart?.();
            return;
          case 'endpoint':
            // Capture stops at the endpoint: the utterance is complete and the turn moves to thinking. Listening on
            // through the answer is what makes an assistant transcribe its own voice.
            stop();
            options.onEndpoint?.();
            return;
          case 'timeout':
            stop();
            options.onTimeout?.();
            return;
          default:
        }
      }, frameMs);
      return true;
    },

    stop,
    state: () => vad,
  };
}
